import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RefreshCw, Save, Sparkles, PenLine, CircleDashed, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { DayBreakdown, fmtH } from './DayBreakdown';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatusBadge = ({ report }) => {
  const { t } = useTranslation();
  const cfg = !report
    ? { Icon: CircleDashed, cls: 'bg-zinc-100 text-zinc-600', label: t('reports.statusNone') }
    : report.edited
      ? { Icon: PenLine, cls: 'bg-[#FEF3C7] text-[#B45309]', label: t('reports.statusEdited') }
      : { Icon: Sparkles, cls: 'bg-[#D1FAE5] text-[#047857]', label: t('reports.statusAuto') };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ${cfg.cls}`} data-testid="report-status-badge">
      <cfg.Icon className="w-3.5 h-3.5" />{cfg.label}
    </span>
  );
};

export const ReportView = ({ data, onReload }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const report = data.report;
  const dirty = text !== (report?.content || '');

  useEffect(() => { setText(report?.content || ''); }, [report?.content, data.user_id, data.week_start]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await onReload(); } catch (e) { toast.error(e.response?.data?.detail || t('reports.error')); } finally { setBusy(false); }
  };

  const generate = () => {
    const force = data.can_edit && report?.edited;
    if (force && !window.confirm(t('reports.confirmOverwrite'))) return;
    run(() => axios.post(`${API_URL}/reports/weekly/generate`, { week_start: data.week_start, user_id: data.user_id, force }), t('reports.generatedToast'));
  };

  const save = () => run(() => axios.put(`${API_URL}/reports/weekly`, { week_start: data.week_start, content: text }), t('reports.savedToast'));

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-6" data-testid="report-view">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="text-lg font-bold text-zinc-900" data-testid="report-user-name">{data.user_name}</h3>
          <p className="text-sm text-zinc-500"><span className="font-semibold text-zinc-800 tabular-nums" data-testid="report-total-hours">{fmtH(data.total_hours)}</span> {t('reports.workedThisWeek')}</p>
        </div>
        <StatusBadge report={report} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section>
          <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">{t('reports.fromTimeEntries')}</h4>
          <DayBreakdown days={data.days} />
        </section>
        <section className="min-w-0">
          <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">{t('reports.reportText')}</h4>
          {data.can_edit ? (
            <>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder={t('reports.placeholder')}
                data-testid="report-textarea" className="font-mono text-[13px] leading-relaxed" />
              <div className="flex flex-wrap gap-2 mt-3">
                <Button onClick={save} disabled={busy || !dirty} data-testid="report-save-btn" className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{t('reports.save')}
                </Button>
                <Button onClick={generate} disabled={busy} variant="outline" data-testid="report-generate-btn" className="rounded-full">
                  <RefreshCw className="w-4 h-4 mr-2" />{t('reports.generateFromEntries')}
                </Button>
              </div>
            </>
          ) : report?.content ? (
            <div className="whitespace-pre-wrap text-sm text-zinc-700 bg-zinc-50 rounded-xl p-4 [overflow-wrap:anywhere]" data-testid="report-readonly-content">{report.content}</div>
          ) : (
            <div className="text-sm text-zinc-400 bg-zinc-50 rounded-xl p-4" data-testid="report-empty">
              {report ? t('reports.emptyReport') : t('reports.notGeneratedYet')}
              {!report && (
                <Button onClick={generate} disabled={busy} variant="outline" size="sm" data-testid="report-generate-btn" className="rounded-full mt-3 flex">
                  <RefreshCw className="w-4 h-4 mr-2" />{t('reports.generateNow')}
                </Button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ReportView;
